import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Typography, Select, Row, Col, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { Title } = Typography;

export default function CugReport() {
    const { authData, backUrl } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [employeeData, setEmployeeData] = useState([]);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    /* ---------------- FETCH MASTER DATA & EMPLOYEES ---------------- */

    useEffect(() => {
        fetchOrganisations();
        fetchEmployeeRecords();
    }, []);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganisations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching organisations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    const fetchEmployeeRecords = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${backUrl}/api/emp/employees/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setEmployeeData(res.data.results || []);
        } catch (error) {
            console.error("Error fetching employee records:", error);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- FILTER HANDLERS ---------------- */

    const handleOrgChange = (value) => {
        setSelectedOrg(value);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDepartments(value);
    };

    const handleDeptChange = (value) => {
        setSelectedDept(value);
        setSelectedDesig(null);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDesignations(value);
    };

    const handleDesigChange = (value) => {
        setSelectedDesig(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    /* ---------------- TABLE DATA MAPPING ---------------- */

    const tableData = useMemo(() => {
        return employeeData.map((item, index) => ({
            key: item.id,
            sno: index + 1, // Will be recalculated after filtering
            organisationId: item.organisation,
            departmentId: item.department,
            designationId: item.designation,

            empId: item.emp_code || item.user?.username || "-",
            name: `${item.first_name || ""} ${item.last_name || ""}`.trim() || "-",
            companyName: item.organisation_name || "-",
            designationName: item.designation_name || "-",
            doj: item.date_of_joining || "-",
            bloodGroup: (item.blood_group === "0" ? "O" : item.blood_group) || "-",
            emergencyContact: item.emergency_contact_number || "-",
            permanentAddress: item.permanent_address_for_communication || "-",
            status: item.status,
            cug: item.cug,
            site: item.site_location
        }));
    }, [employeeData]);

    /* ---------------- APPLY FILTERS ---------------- */

    const filteredTableData = useMemo(() => {
        let filtered = tableData.filter(row => {
            if (selectedOrg && row.organisationId !== selectedOrg) return false;
            if (selectedDept && row.departmentId !== selectedDept) return false;
            if (selectedDesig && row.designationId !== selectedDesig) return false;

            if (searchText.trim()) {
                const searchLower = searchText.toLowerCase().trim();
                const empIdMatch = row.empId.toLowerCase().includes(searchLower);
                const nameMatch = row.name.toLowerCase().includes(searchLower);
                if (!empIdMatch && !nameMatch) return false;
            }

            return true;
        });

        // Re-assign S.No after filtering
        return filtered.map((row, index) => ({
            ...row,
            sno: index + 1
        }));
    }, [tableData, selectedOrg, selectedDept, selectedDesig, searchText]);

    /* ---------------- TABLE COLUMNS ---------------- */

    const columns = [
        {
            title: "SNO",
            dataIndex: "sno",
            width: 70,
            fixed: "left"
        },
        { title: "EMP ID", dataIndex: "empId", width: 120 },
        { title: "NAME", dataIndex: "name", width: 200 },
        { title: "Company Name", dataIndex: "companyName", width: 250 },
        { title: "DESIGNATION", dataIndex: "designationName", width: 160 },
        { title: "DOJ", dataIndex: "doj", width: 120 },
        { title: "SITE", dataIndex: "site", width: 120 },
        { title: "CUG", dataIndex: "cug", width: 140 },
        { title: "EMPLOYMENT STATUS", dataIndex: "status", width: 300 },
    ];

    /* ---------------- EXCEL DOWNLOAD ---------------- */

    const downloadExcel = () => {
        const excelData = filteredTableData.map(row => ({
            "SNO": row.sno,
            "EMP ID": row.empId,
            "NAME": row.name,
            "Company Name": row.companyName,
            "DESIGNATION": row.designationName,
            "DOJ": row.doj,
            "BLOOD GROUP": row.bloodGroup,
            "E_CONTACT NO": row.emergencyContact,
            "PERMANENT ADDRESS": row.permanentAddress
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ID Card Report");

        // Style header row
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_cell({ r: 0, c: C });
            if (!worksheet[address]) continue;
            worksheet[address].s = {
                fill: { fgColor: { rgb: "0e497b" } },
                font: { color: { rgb: "FFFFFF" }, bold: true },
                alignment: { horizontal: "center" }
            };
        }

        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer]), "ID_Card_Report.xlsx");
    };

    return (
        <>
            <Title level={4}>CUG Report</Title>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={5}>
                    <Select
                        placeholder="Select Organisation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        value={selectedOrg}
                        onChange={handleOrgChange}
                        style={{ width: "100%" }}
                    >
                        {organisations.map(org => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Department"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedOrg}
                        value={selectedDept}
                        onChange={handleDeptChange}
                        style={{ width: "100%" }}
                    >
                        {departments.map(dept => (
                            <Select.Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Designation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedDept}
                        value={selectedDesig}
                        onChange={handleDesigChange}
                        style={{ width: "100%" }}
                    >
                        {designations.map(des => (
                            <Select.Option key={des.id} value={des.id}>
                                {des.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Input
                        placeholder="Search by Employee ID or Name"
                        allowClear
                        value={searchText}
                        onChange={handleSearchChange}
                        style={{ width: "100%" }}
                    />
                </Col>

                <Col span={4} style={{ textAlign: "right" }}>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadExcel}
                        style={{ backgroundColor: "#0e497b" }}
                    >
                        Download Excel
                    </Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={filteredTableData}
                loading={loading}
                bordered
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: filteredTableData.length,
                    onChange: page => setCurrentPage(page),
                    showSizeChanger: false
                }}
                scroll={{ x: "max-content" }}
                components={{
                    header: {
                        cell: props => (
                            <th {...props} style={{ background: "#0e497b", color: "#fff", textAlign: "center" }}>
                                {props.children}
                            </th>
                        )
                    }
                }}
            />
        </>
    );
}